import type { Decimal } from 'decimal.js'
import type { CommissionProductType, CommissionTier, CommissionTierMode } from './commissions.js'
import { money, toMoney } from './money.js'

/**
 * The commission calculator (ADR-049), shared by the API (authoritative) and the web (plan previews). Pure.
 *
 * Each line of an earner's month is priced at the most specific rate of the plan: a rate for its category (or
 * the nearest ancestor category) and its product type, then for its category, then for its product type, then
 * the plan's rate, which monthly tiers replace: WHOLE pays at the rate of the highest tier the month reached,
 * MARGINAL at the effective rate of the month priced slice by slice. The month reached is every priced line
 * (credit notes lower it); clawbacks of earlier months keep the amount they were paid at.
 */

export interface CommissionCalcPlan {
  rate: string
  tierMode: CommissionTierMode
  tiers: readonly CommissionTier[]
  rates: readonly { productType: CommissionProductType | null; categoryId: string | null; rate: string }[]
}

export interface CommissionCalcLine {
  key: string
  /** Signed: credit notes are negative. */
  base: string
  productType: CommissionProductType | null
  /** The line's category, then its ancestors up to the root. */
  categoryPath: readonly string[]
  /** A clawback: the amount is fixed (the negated amount once paid) and never re-priced. */
  fixedAmount?: string | null
}

export interface CommissionCalcResult {
  /** The month's priced base (clawbacks excluded), which the tiers measure. */
  monthBase: string
  /** The rate the plan's default lines got: the tier rate, or the plan's rate without tiers. */
  defaultRate: string
  lines: { key: string; rate: string; amount: string }[]
  earned: string
}

/** The plan rate a line gets, or null when the plan's default (tier) rate applies. */
export function specificRate(plan: CommissionCalcPlan, line: CommissionCalcLine): string | null {
  for (const categoryId of line.categoryPath) {
    const both = plan.rates.find((r) => r.categoryId === categoryId && r.productType === line.productType)
    if (both && line.productType !== null) return both.rate
    const category = plan.rates.find((r) => r.categoryId === categoryId && r.productType === null)
    if (category) return category.rate
  }
  if (line.productType) {
    const type = plan.rates.find((r) => r.categoryId === null && r.productType === line.productType)
    if (type) return type.rate
  }
  return null
}

/** The month's default rate from the tiers (the plan's rate without tiers). */
export function tierRate(plan: CommissionCalcPlan, monthBase: Decimal): Decimal {
  const tiers = [...plan.tiers].sort((a, b) => Number(a.fromAmount) - Number(b.fromAmount))
  const first = tiers[0]
  if (!first) return money(plan.rate)
  if (!monthBase.greaterThan(0)) return money(first.rate)
  if (plan.tierMode === 'WHOLE') {
    let rate = money(first.rate)
    for (const t of tiers) if (monthBase.greaterThanOrEqualTo(t.fromAmount)) rate = money(t.rate)
    return rate
  }
  // MARGINAL: each slice of the month at its tier's rate; the default lines get the effective rate.
  let commission = money(0)
  tiers.forEach((t, i) => {
    const next = tiers[i + 1]
    const upTo = next && monthBase.greaterThan(next.fromAmount) ? money(next.fromAmount) : monthBase
    const slice = upTo.minus(t.fromAmount)
    if (slice.greaterThan(0)) commission = commission.plus(slice.times(t.rate))
  })
  return commission.dividedBy(monthBase).toDecimalPlaces(4)
}

export function calculateCommission(
  plan: CommissionCalcPlan,
  lines: readonly CommissionCalcLine[],
): CommissionCalcResult {
  const priced = lines.filter((l) => l.fixedAmount === undefined || l.fixedAmount === null)
  const monthBase = priced.reduce((s, l) => s.plus(l.base), money(0))
  const defaultRate = tierRate(plan, monthBase)
  const out = lines.map((l) => {
    if (l.fixedAmount !== undefined && l.fixedAmount !== null) {
      const base = money(l.base)
      const rate = base.isZero() ? money(0) : money(l.fixedAmount).dividedBy(base).times(100)
      return { key: l.key, rate: rate.toDecimalPlaces(4).toString(), amount: toMoney(l.fixedAmount) }
    }
    const specific = specificRate(plan, l)
    const rate = specific === null ? defaultRate : money(specific)
    return {
      key: l.key,
      rate: rate.toDecimalPlaces(4).toString(),
      amount: toMoney(money(l.base).times(rate).dividedBy(100)),
    }
  })
  return {
    monthBase: toMoney(monthBase),
    defaultRate: defaultRate.toDecimalPlaces(4).toString(),
    lines: out,
    earned: toMoney(out.reduce((s, l) => s.plus(l.amount), money(0))),
  }
}
